import { Outlet, NavLink, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { LayoutDashboard, Car, MessageSquare, Users, Settings, LogOut, ExternalLink, FileText, Gauge, Sparkles, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import Logo from "@/components/Logo";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t } = useLang();

  const items = [
    { to: "/admin", icon: LayoutDashboard, label: t.admin.dashboard, end: true },
    { to: "/admin/vehicles", icon: Car, label: t.admin.vehicles },
    { to: "/admin/showroom", icon: ImageIcon, label: "Showroom" },
    { to: "/admin/simulator", icon: Gauge, label: "Coaching Sim" },
    { to: "/admin/testimonials", icon: Users, label: t.admin.testimonials },
    { to: "/admin/blog", icon: FileText, label: "Blog" },
    { to: "/admin/contacts", icon: MessageSquare, label: t.admin.contacts },
    { to: "/admin/leads", icon: Sparkles, label: "Leads" },
    { to: "/admin/site", icon: Settings, label: t.admin.site },
  ];

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-[#0D0D0D] text-white grid lg:grid-cols-[260px_1fr]">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="googlebot" content="noindex, nofollow" />
        <title>Admin · KV International</title>
      </Helmet>
      <aside className="bg-black border-r border-white/10 p-6 flex flex-col">
        <Link to="/" className="flex items-center gap-3 mb-10">
          <Logo asLink={false} withText={false} size="w-12 h-12" />
          <span className="text-white/70 text-[10px] uppercase tracking-[0.3em] font-brand">Admin</span>
        </Link>

        <nav className="space-y-1 flex-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`admin-nav-${it.to.split("/").pop()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-md text-sm transition-colors ${
                  isActive ? "bg-[#C8A75A]/10 text-[#C8A75A] border border-[#C8A75A]/30" : "text-white/70 hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <it.icon className="w-4 h-4" strokeWidth={1.4} />
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="pt-6 border-t border-white/10 space-y-2">
          <Link to="/" className="flex items-center gap-2 text-xs text-white/50 hover:text-white px-2 py-2">
            <ExternalLink className="w-3.5 h-3.5" /> Voir le site
          </Link>
          <div className="px-2 py-2 text-xs text-white/50 truncate">{user?.email}</div>
          <button data-testid="admin-logout" onClick={logout} className="w-full flex items-center gap-2 text-xs text-white/60 hover:text-[#C8A75A] px-2 py-2">
            <LogOut className="w-3.5 h-3.5" /> {t.nav.logout}
          </button>
        </div>
      </aside>

      <main className="p-8 lg:p-12">
        <Outlet />
      </main>
    </div>
  );
}
